import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, Download, Clock, Timer, Info, CalendarPlus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { downloadIcs, googleCalendarUrl } from "@/lib/examIcs";

const OP = { ANTH: "Ο.Π. Ανθρωπιστικών", THET: "Ο.Π. Θετικών", YG: "Ο.Π. Υγείας", OIK: "Ο.Π. Οικονομίας & Πληροφορικής", GP: "Γενικής Παιδείας" };
const COLORS = { [OP.ANTH]: "#B45309", [OP.THET]: "#0A1A4A", [OP.YG]: "#B91C1C", [OP.OIK]: "#047857", [OP.GP]: "#475569" };

const GEL = [
  { date: "2027-05-28", items: [{ s: "Νεοελληνική Γλώσσα & Λογοτεχνία", g: [OP.GP] }] },
  { date: "2027-05-31", items: [{ s: "Αρχαία Ελληνικά", g: [OP.ANTH] }, { s: "Μαθηματικά", g: [OP.THET, OP.OIK] }, { s: "Βιολογία", g: [OP.YG] }] },
  { date: "2027-06-02", items: [{ s: "Λατινικά", g: [OP.ANTH] }, { s: "Χημεία", g: [OP.THET, OP.YG] }, { s: "Πληροφορική", g: [OP.OIK] }] },
  { date: "2027-06-04", items: [{ s: "Ιστορία", g: [OP.ANTH] }, { s: "Φυσική", g: [OP.THET, OP.YG] }, { s: "Οικονομία", g: [OP.OIK] }] },
];

const EPAL = [
  { date: "2027-05-29", items: [{ s: "Νέα Ελληνικά" }] },
  { date: "2027-06-01", time: "10:00", items: [{ s: "Μαθηματικά (Άλγεβρα)" }] },
  { date: "2027-06-03", items: [{ s: "Ανατομία–Φυσιολογία ΙΙ" }, { s: "Αρχές Οικονομικής Θεωρίας (ΑΟΘ)" }, { s: "Δίκτυα Υπολογιστών" }, { s: "Αρχές Βιολογικής Γεωργίας" }] },
  { date: "2027-06-05", items: [{ s: "Ηλεκτροτεχνία 2" }, { s: "Αρχιτεκτονικό Σχέδιο (4 ώρες)" }, { s: "Προγραμματισμός Υπολογιστών" }, { s: "Ιστορία Σύγχρονης Τέχνης" }] },
  { date: "2027-06-07", items: [{ s: "Υγιεινή" }, { s: "Ναυτικό Δίκαιο – Διεθνείς Κανονισμοί στη Ναυτιλία" }, { s: "Αρχές Οργάνωσης & Διοίκησης (ΑΟΔ)" }, { s: "Στοιχεία Μηχανών" }] },
  { date: "2027-06-09", items: [{ s: "Ηλεκτρικές Μηχανές" }, { s: "Σύγχρονες Γεωργικές Επιχειρήσεις" }, { s: "Ναυσιπλοΐα ΙΙ" }, { s: "Ναυτικές Μηχανές" }] },
  { date: "2027-06-11", items: [{ s: "Τεχνολογία Υλικών" }, { s: "Οικοδομική" }, { s: "Μηχανές Εσωτερικής Καύσης ΙΙ (ΜΕΚ ΙΙ)" }, { s: "Ψηφιακά Συστήματα" }] },
  { date: "2027-06-12", items: [{ s: "Στοιχεία Ψύξης–Κλιματισμού" }, { s: "Κινητήρες Αεροσκαφών" }, { s: "Στοιχεία Σχεδιασμού Κεντρικών Θερμάνσεων" }] },
];

const SPECIAL = [
  { date: "2027-06-14", time: "10:00", items: [{ s: "Αγγλικά" }] },
  { date: "2027-06-15", items: [{ s: "Ελεύθερο Σχέδιο (6 ώρες)" }] },
  { date: "2027-06-16", items: [{ s: "Γραμμικό Σχέδιο (6 ώρες)" }] },
  { date: "2027-06-17", items: [{ s: "Γαλλικά" }] },
  { date: "2027-06-18", items: [{ s: "Γερμανικά" }] },
  { date: "2027-06-19", time: "10:00", items: [{ s: "Μουσική Αντίληψη, Θεωρία & Αρμονία (3,5 ώρες)" }] },
  { date: "2027-06-22", items: [{ s: "Μουσική Εκτέλεση & Ερμηνεία (4'–6' ανά υποψήφιο)" }] },
  { date: "2027-06-23", items: [{ s: "Ισπανικά" }] },
  { date: "2027-06-24", items: [{ s: "Ιταλικά" }] },
];

const TABS = [
  { id: "gel", label: "ΓΕΛ", days: GEL, note: "Έναρξη 08:30 (προσέλευση έως 08:00), διάρκεια 3 ώρες. Κοινά θέματα για ημερήσια & εσπερινά ΓΕΛ." },
  { id: "epal", label: "ΕΠΑΛ", days: EPAL, note: "Έναρξη 08:30 (εκτός Άλγεβρας: 10:00), διάρκεια 3 ώρες — Αρχιτεκτονικό Σχέδιο 4 ώρες." },
  { id: "special", label: "Ειδικά & Μουσικά", days: SPECIAL, note: "Ξένες γλώσσες 3 ώρες, Σχέδια 6 ώρες. ΤΕΦΑΑ: υγειονομική εξέταση & πρακτική δοκιμασία 14–25/6/2027." },
];

const GROUPS = [
  { id: "all", label: "Όλες οι ομάδες", ops: null },
  { id: "anth", label: "Ανθρωπιστικών", ops: [OP.ANTH] },
  { id: "thet", label: "Θετικών", ops: [OP.THET] },
  { id: "yg", label: "Υγείας", ops: [OP.YG] },
  { id: "oik", label: "Οικονομίας & Πληροφορικής", ops: [OP.OIK] },
];
const STORAGE = "exam_group_v1";

const WD = ["Κυριακή", "Δευτέρα", "Τρίτη", "Τετάρτη", "Πέμπτη", "Παρασκευή", "Σάββατο"];
const MON = ["Ιαν", "Φεβ", "Μαρ", "Απρ", "Μαΐ", "Ιουν", "Ιουλ", "Αυγ", "Σεπ", "Οκτ", "Νοε", "Δεκ"];
const parse = (iso) => new Date(iso + "T08:30:00+03:00");

function DayCard({ d, i, showGroups }) {
  const dt = parse(d.date);
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.35, delay: Math.min(i, 6) * 0.05 }}
      data-testid={`exam-day-${d.date}`}
      className="flex gap-4 bg-white border border-border rounded-sm p-4"
    >
      <div className="w-16 shrink-0 text-center border-r border-border pr-4">
        <div className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground">{WD[dt.getDay()].slice(0, 3)}</div>
        <div className="font-heading font-extrabold text-3xl leading-none text-primary">{dt.getDate()}</div>
        <div className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground">{MON[dt.getMonth()]}</div>
        <div className="mt-1 inline-flex items-center gap-1 font-mono-accent text-[10px] text-accent"><Clock className="h-3 w-3" />{d.time || "08:30"}</div>
      </div>
      <ul className="flex-1 min-w-0 space-y-2">
        {d.items.map((it) => (
          <li key={it.s} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
            <span className="font-medium text-sm leading-snug">{it.s}</span>
            {showGroups && it.g && (
              <span className="flex flex-wrap gap-1">
                {it.g.map((g) => (
                  <span key={g} className="font-mono-accent text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm text-white" style={{ background: COLORS[g] }}>{g}</span>
                ))}
              </span>
            )}
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

export default function ExamSchedule() {
  const [tab, setTab] = useState("gel");
  const [group, setGroup] = useState(() => localStorage.getItem(STORAGE) || "all");
  const current = TABS.find((t) => t.id === tab);
  const g = GROUPS.find((x) => x.id === group) || GROUPS[0];
  const pick = (id) => { setGroup(id); localStorage.setItem(STORAGE, id); };
  const calLabel = tab === "gel" ? (g.ops ? `ΓΕΛ – ${g.label}` : "ΓΕΛ") : current.label;

  const days = useMemo(() => {
    if (tab !== "gel" || !g.ops) return current.days;
    return GEL.map((d) => ({ ...d, items: d.items.filter((it) => it.g.includes(OP.GP) || it.g.some((x) => g.ops.includes(x))) })).filter((d) => d.items.length);
  }, [tab, g, current]);
  const daysLeft = useMemo(() => Math.max(0, Math.ceil((parse(GEL[0].date) - Date.now()) / 86400000)), []);

  return (
    <section id="exams-2027" data-testid="exam-schedule-section" className="py-20 md:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
          <div className="max-w-2xl">
            <div className="font-mono-accent text-xs uppercase tracking-[0.2em] text-accent mb-4">// Πανελλαδικές 2027</div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight flex items-center gap-3">
              <CalendarDays className="h-8 w-8 text-primary" strokeWidth={1.5} /> Το πρόγραμμα των Πανελλαδικών 2027.
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Επίσημο πρόγραμμα του Υπουργείου Παιδείας (ανακοίνωση 11/9/2026) για ΓΕΛ, ΕΠΑΛ, Ειδικά & Μουσικά μαθήματα. Οι εξετάσεις ΓΕΛ ξεκινούν την <strong className="text-foreground">Παρασκευή 28 Μαΐου 2027</strong>.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <div data-testid="exam-countdown" className="bg-primary text-primary-foreground rounded-sm px-5 py-4 flex items-center gap-4">
              <Timer className="h-8 w-8 text-accent" strokeWidth={1.5} />
              <div>
                <div className="font-heading font-extrabold text-3xl leading-none">{daysLeft}</div>
                <div className="font-mono-accent text-[10px] uppercase tracking-widest text-white/60 mt-1">ημέρες έως τις εξετάσεις ΓΕΛ</div>
              </div>
            </div>
            <a
              data-testid="exam-pdf-download"
              href="/programma-panelladikon-2027.pdf"
              download="programma-panelladikon-2027.pdf"
              className="inline-flex items-center justify-center gap-2 border border-border bg-white font-mono-accent text-xs uppercase tracking-widest px-5 py-4 rounded-sm hover:border-primary transition-colors"
            >
              <Download className="h-4 w-4" /> Επίσημο PDF
            </a>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="exam-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`exam-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`h-10 px-5 rounded-sm font-mono-accent text-xs uppercase tracking-widest border transition-colors ${tab === t.id ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border hover:border-primary"}`}
            >
              {t.label} <span className={`ml-1 ${tab === t.id ? "text-white/60" : "text-muted-foreground"}`}>({t.days.length})</span>
            </button>
          ))}
        </div>

        {tab === "gel" && (
          <div className="mb-6 bg-white border border-border rounded-sm p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="exam-group-picker">
            <span className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">Η Ομάδα Προσανατολισμού μου:</span>
            <div className="flex flex-wrap gap-2">
              {GROUPS.map((x) => (
                <button
                  key={x.id}
                  data-testid={`exam-group-${x.id}`}
                  onClick={() => pick(x.id)}
                  className={`h-9 px-4 rounded-sm font-mono-accent text-[11px] uppercase tracking-wider border transition-colors ${group === x.id ? "text-white border-transparent" : "bg-white border-border hover:border-primary"}`}
                  style={group === x.id ? { background: x.ops ? COLORS[x.ops[0]] : "#0A1A4A" } : undefined}
                >
                  {x.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {tab === "gel" && g.ops && (
          <p data-testid="exam-group-summary" className="mb-4 text-sm text-muted-foreground">
            Ως μαθητής/τρια <strong className="text-foreground">{g.label}</strong> εξετάζεσαι σε <strong className="text-foreground">{days.reduce((n, d) => n + d.items.length, 0)} μαθήματα</strong> σε {days.length} ημέρες:
            {" "}{days.flatMap((d) => d.items.map((it) => it.s)).join(" · ")}.
          </p>
        )}

        <div className="mb-6 bg-primary text-primary-foreground rounded-sm p-4 md:p-5 flex flex-col lg:flex-row lg:items-center gap-4" data-testid="exam-calendar-export">
          <div className="flex items-start gap-3 flex-1">
            <CalendarPlus className="h-6 w-6 text-accent shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <div className="font-heading font-bold">Πρόσθεσε τις εξετάσεις στο ημερολόγιό σου</div>
              <div className="text-xs text-white/70 mt-0.5">{days.length} ημέρες ({calLabel}) με υπενθύμιση 1 ημέρα πριν. Το .ics ανοίγει σε Apple Calendar, Outlook και Google Calendar (Εισαγωγή).</div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <button
              data-testid="exam-ics-download"
              onClick={() => { downloadIcs(days, calLabel, `panelladikes-2027-${tab}${g.ops && tab === "gel" ? `-${group}` : ""}.ics`); toast.success("Το αρχείο ημερολογίου κατέβηκε."); }}
              className="inline-flex items-center justify-center gap-2 h-11 px-5 bg-white text-primary font-mono-accent text-xs uppercase tracking-widest rounded-sm hover:bg-accent hover:text-white transition-colors"
            >
              <Download className="h-4 w-4" /> Apple / Outlook (.ics)
            </button>
            <div className="relative group">
              <button data-testid="exam-google-menu" className="w-full inline-flex items-center justify-center gap-2 h-11 px-5 border border-white/30 font-mono-accent text-xs uppercase tracking-widest rounded-sm hover:bg-white/10 transition-colors">
                <ExternalLink className="h-4 w-4" /> Google Calendar
              </button>
              <div className="absolute right-0 top-full mt-1 z-10 hidden group-hover:block group-focus-within:block bg-white text-foreground border border-border rounded-sm shadow-lg min-w-[260px] p-2">
                <div className="px-2 py-1 font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground">Μία ημέρα τη φορά:</div>
                {days.map((d) => (
                  <a key={d.date} data-testid={`exam-google-${d.date}`} href={googleCalendarUrl(d, calLabel)} target="_blank" rel="noopener noreferrer" className="block px-2 py-1.5 text-sm rounded-sm hover:bg-secondary">
                    <span className="font-mono-accent text-xs text-primary mr-2">{d.date.split("-").reverse().slice(0, 2).join("/")}</span>{d.items.map((i) => i.s).join(" · ").slice(0, 48)}{d.items.map((i) => i.s).join(" · ").length > 48 ? "…" : ""}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div key={`${tab}-${group}`} className="grid md:grid-cols-2 gap-4" data-testid="exam-days">
          {days.map((d, i) => <DayCard key={d.date} d={d} i={i} showGroups={tab === "gel" && !g.ops} />)}
        </div>

        <p data-testid="exam-note" className="mt-6 flex gap-2 text-xs text-muted-foreground leading-relaxed">
          <Info className="h-4 w-4 shrink-0 mt-0.5" /> {current.note}
        </p>
      </div>
    </section>
  );
}
